/**
 * TrameStudentNextSteps.jsx — Bloc « prochaines étapes » après auto-évaluation.
 *
 * Rendu uniquement en mode élève, en fin de trame. Objectif : simplifier
 * la sortie du flow session. Propose 2 actions claires et hiérarchisées :
 *
 *   1) Continue dans ton livret d'apprentissage
 *      → link direct vers le livret correspondant à la trame courante
 *
 *   2) Sauvegarde ta progression (UNIQUEMENT pour les guests)
 *      → CTA vers /register (ou /login) pour synchro cloud
 *
 * Design volontairement épuré : 2 cartes empilées sur mobile, en grille sur
 * desktop. Zéro doublon avec le bandeau TrameLivretCross en haut de page.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList, ArrowRight, Cloud, UserPlus, ShieldCheck,
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';

/**
 * Mapping trameKey → route du livret correspondant.
 * Étend facilement à d'autres formations (AM, etc.).
 */
const LIVRET_ROUTES = {
  '125':        { to: '/livret-125',                label: 'Ton livret 125 cm³' },
  'plateau':    { to: '/livret-plateau',            label: 'Ton livret Plateau A1 / A2' },
  'passerelle': { to: '/livret-passerelle-a2-a',    label: 'Ton livret Passerelle A2 → A' },
  'am':         { to: '/livret-am',                 label: 'Ton livret Permis AM' },
};

export default function TrameStudentNextSteps({ trameKey, color = '#e11d48', evaluatedCount = 0, masteredCount = 0 }) {
  const { user } = useApp();
  const livret = LIVRET_ROUTES[trameKey];
  const isGuest = !user;

  // Si on n'a ni livret ni CTA à afficher, on cache le bloc.
  if (!livret && !isGuest) return null;

  return (
    <section
      className="mt-6 print:hidden"
      data-testid="student-next-steps"
      aria-label="Prochaines étapes après la session"
    >
      <div className="mb-3">
        <h3
          className="text-white font-black text-lg sm:text-xl leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Et maintenant ?
        </h3>
        <p className="text-white/60 text-[12.5px] mt-1">
          Deux gestes rapides pour prolonger ta session.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {/* 1. Livret d'apprentissage — carte principale */}
        {livret && (
          <Link
            to={livret.to}
            className="group rounded-2xl border p-4 flex items-start gap-3 hover:bg-white/[0.02] active:scale-[0.99] transition"
            style={{ borderColor: `${color}55`, background: `linear-gradient(135deg, ${color}10 0%, transparent 70%)` }}
            data-testid="next-step-livret"
          >
            <span
              className="w-11 h-11 rounded-xl border flex items-center justify-center flex-shrink-0"
              style={{ borderColor: `${color}44`, background: `${color}18`, color }}
            >
              <ClipboardList className="w-5 h-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest mb-0.5" style={{ color }}>
                Étape 1 · Continue le livret
              </div>
              <div className="text-white font-black text-[14.5px] leading-tight">
                {livret.label}
              </div>
              <p className="text-white/70 text-[12.5px] mt-1 leading-relaxed">
                {!isGuest && masteredCount > 0
                  ? <>Tes <strong className="text-white">{masteredCount} compétence{masteredCount > 1 ? 's' : ''} maîtrisée{masteredCount > 1 ? 's' : ''}</strong> apparaîtront directement dans ton livret comme <em>vues en session</em>.</>
                  : <>Coche ce que tu as travaillé, ajoute tes notes personnelles et retrouve tout ton parcours au même endroit.</>
                }
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-white/40 flex-shrink-0 mt-1 group-hover:text-white transition" />
          </Link>
        )}

        {/* 2a. GUEST → sauvegarde cloud (CTA inscription) */}
        {isGuest && (
          <div
            className="rounded-2xl border p-4 flex items-start gap-3"
            style={{ borderColor: '#10b98155', background: 'linear-gradient(135deg, #10b98110 0%, transparent 70%)' }}
            data-testid="next-step-signup"
          >
            <span
              className="w-11 h-11 rounded-xl border border-emerald-500/40 bg-emerald-500/15 text-emerald-300 flex items-center justify-center flex-shrink-0"
            >
              <Cloud className="w-5 h-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-0.5">
                Étape 2 · Garde ton avancement
              </div>
              <div className="text-white font-black text-[14.5px] leading-tight">
                Sauvegarde ta progression
              </div>
              <p className="text-white/70 text-[12.5px] mt-1 leading-relaxed">
                Sans compte, ton auto-évaluation reste sur ce téléphone uniquement. Avec un compte, elle rejoint automatiquement ton livret et suit ta progression.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-[11.5px] font-black uppercase tracking-widest transition"
                  data-testid="next-step-register"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Créer mon compte
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white/85 text-[11.5px] font-black uppercase tracking-widest transition"
                  data-testid="next-step-login"
                >
                  J'ai déjà un compte
                </Link>
              </div>
              <div className="mt-2.5 inline-flex items-center gap-1.5 text-[10.5px] text-white/55">
                <ShieldCheck className="w-3 h-3" /> Gratuit · aucune donnée revendue
              </div>
            </div>
          </div>
        )}

        {/* 2b. CONNECTÉ → confirmation sync (info + accès livret) */}
        {!isGuest && (
          <div
            className="rounded-2xl border p-4 flex items-start gap-3"
            style={{ borderColor: '#10b98155', background: 'linear-gradient(135deg, #10b98110 0%, transparent 70%)' }}
            data-testid="next-step-synced"
          >
            <span
              className="w-11 h-11 rounded-xl border border-emerald-500/40 bg-emerald-500/15 text-emerald-300 flex items-center justify-center flex-shrink-0"
            >
              <Cloud className="w-5 h-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-0.5">
                Étape 2 · Ta progression suit avec toi
              </div>
              <div className="text-white font-black text-[14.5px] leading-tight">
                {evaluatedCount > 0
                  ? `${evaluatedCount} compétence${evaluatedCount > 1 ? 's' : ''} enregistrée${evaluatedCount > 1 ? 's' : ''} sur ton compte`
                  : 'Tes évaluations sont sauvegardées'}
              </div>
              <p className="text-white/70 text-[12.5px] mt-1 leading-relaxed">
                Ton auto-évaluation est déjà synchronisée dans le cloud. Elle rejoindra ton livret et ton moniteur pourra la consulter au prochain cours pour ajuster.
              </p>
              <div className="mt-2.5 inline-flex items-center gap-1.5 text-[10.5px] text-emerald-200/85">
                <ShieldCheck className="w-3 h-3" /> Connecté · sauvegarde automatique active
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
